'use client';

import { useEffect, useRef, useMemo } from 'react';
import type { Render } from '@/types/render';
import { setRendersForConcept } from '../model/renders-cache';

/**
 * Hydrate the TanStack Query renders cache with the concept-detail payload.
 * Re-runs when the underlying render IDs change (e.g., after angle replacement
 * or a new render lands via WS).
 */
export function useInitializeRendersFromData(
  conceptId: string | undefined,
  renders: Record<string, Render[]> | undefined
) {
  const initializedConceptRef = useRef<string | null>(null);
  const previousRendersHashRef = useRef<string | null>(null);

  const rendersHash = useMemo(() => {
    if (!renders) return null;
    return Object.values(renders)
      .flatMap((rs) => rs.map((r) => r.id))
      .sort()
      .join(',');
  }, [renders]);

  useEffect(() => {
    if (!conceptId || !renders) return;

    const isSameConcept = initializedConceptRef.current === conceptId;
    const isSameRenders = previousRendersHashRef.current === rendersHash;
    if (isSameConcept && isSameRenders) return;

    const flat: Render[] = [];
    Object.values(renders).forEach((angleRenders) => {
      if (Array.isArray(angleRenders)) flat.push(...angleRenders);
    });

    setRendersForConcept(conceptId, flat);

    initializedConceptRef.current = conceptId;
    previousRendersHashRef.current = rendersHash;
  }, [conceptId, rendersHash, renders]);
}
